import { Children, isValidElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Listbox } from '@headlessui/react';
import { Search } from 'lucide-react';
import { matchesSearch } from '../directory/directoryModel';
import './CleanSelect.css';

const labelText = children => Children.toArray(children).map(child => isValidElement(child) ? labelText(child.props.children) : String(child)).join('');
// A search box shows only when there are more options than fit at a glance.
export const SEARCH_MIN = 8;

// `search`: the search box's placeholder ("Search teachers"). With more than
// SEARCH_MIN options, the menu shows the "All" option (value ''), then the
// search box, then the options that match (Sandy 2026-10-08).
export default function CleanSelect({ value, onChange, children, disabled, 'aria-label': label, className = '', search = null }) {
  const options = Children.toArray(children).filter(isValidElement).map(option => ({ value: option.props.value ?? labelText(option.props.children), label: option.props.children, disabled: option.props.disabled }));
  const selected = options.find(option => String(option.value) === String(value));
  const button = useRef(null);
  return <Listbox as="div" className={`admin-select ${className}`} value={value} disabled={disabled} onChange={next => onChange({ target: { value: next } })}>
    {({ open }) => <><Listbox.Button ref={button} aria-label={label} className="admin-select-trigger"><span>{selected?.label || 'Choose…'}</span><span aria-hidden="true" className="admin-select-chevron" /></Listbox.Button>{open && <SelectOptions button={button} options={options} label={label} search={search && options.length > SEARCH_MIN ? search : null} />}</>}
  </Listbox>;
}
function SelectOptions({ button, options, label, search }) {
  const menu = useRef(null);
  const field = useRef(null);
  const [position, setPosition] = useState(null);
  const [query, setQuery] = useState('');
  // After typing, the first match is the one Enter picks (and looks chosen),
  // until the arrows or the mouse choose another row.
  const [hint, setHint] = useState(false);
  const pinned = search ? options.filter(option => option.value === '') : [];
  const rest = search ? options.filter(option => option.value !== '') : options;
  const shown = query.trim() ? rest.filter(option => matchesSearch(labelText(option.label), query)) : rest;
  useLayoutEffect(() => {
    // The width is measured once, on the full list, so the menu does not narrow
    // as a search hides the long names. A scroll only moves it.
    const measure = event => {
      const rect = button.current?.getBoundingClientRect(); const box = menu.current; if (!rect || !box) return;
      if (event?.type === 'scroll' && box.style.width) {
        setPosition(p => (p ? { ...p, left: Math.max(8, Math.min(rect.left, (document.documentElement.clientWidth || window.innerWidth) - box.offsetWidth - 8)), ...(p.bottom != null ? { bottom: window.innerHeight - rect.top + 5 } : { top: rect.bottom + 5 }) } : p));
        return;
      }
      box.style.width = '';
      // At least as wide as the button, and wide enough that each option fits on
      // one line, up to the window width (Sandy 2026-09-28: a narrow "Math" box
      // broke "All subjects" into "All subje cts"). Kept inside the window.
      // The height is set before the width is read, so a scrollbar is counted.
      const below = window.innerHeight - rect.bottom - 12;
      const up = below < 180 && rect.top > below;
      const maxHeight = Math.max(80, Math.min(search ? 400 : 280, up ? rect.top - 12 : below));
      // The page's visible width leaves out a desktop scrollbar (window.innerWidth counts it).
      const vw = document.documentElement.clientWidth || window.innerWidth;
      box.style.minWidth = `${Math.min(rect.width, vw - 16)}px`;
      box.style.maxWidth = `${vw - 16}px`;
      box.style.maxHeight = `${maxHeight}px`;
      const width = box.offsetWidth;
      box.style.width = `${width}px`;
      setPosition({ left: Math.max(8, Math.min(rect.left, vw - width - 8)), maxHeight, ...(up ? { bottom: window.innerHeight - rect.top + 5 } : { top: rect.bottom + 5 }) });
    };
    measure(); window.addEventListener('resize', measure); window.addEventListener('scroll', measure, true);
    return () => { window.removeEventListener('resize', measure); window.removeEventListener('scroll', measure, true); };
  }, [button, search]);
  // Headless UI puts focus back on the button one frame after the menu closes,
  // but that call is cancelled because the menu unmounts first, so focus fell
  // to the page. Put it back when it was in the menu (Escape, Enter, a pick);
  // a click elsewhere keeps focus where it went.
  useLayoutEffect(() => {
    const box = menu.current;
    return () => { if (box?.contains(document.activeElement)) button.current?.focus({ preventScroll: true }); };
  }, [button]);
  // With a search box, the cursor goes in it once Headless UI has focused the menu.
  useEffect(() => {
    if (!search) return undefined;
    const frame = requestAnimationFrame(() => field.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [search]);
  useEffect(() => { if (menu.current) menu.current.scrollTop = 0; }, [query]);
  // Typing stays in the box (the menu's own type-ahead and Space would pick an
  // option). The arrows, Escape and Enter go on to the menu; Enter right after
  // typing picks the first match.
  const onKeyDown = event => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { setHint(false); return; }
    if (event.key === 'Escape' || event.key === 'Tab') return;
    if (event.key === 'Enter') {
      if (!hint) return;
      event.preventDefault();
      event.stopPropagation();
      menu.current?.querySelector('[data-match]:not([aria-disabled="true"])')?.click();
      return;
    }
    event.stopPropagation();
  };
  const item = (option, match) => <Listbox.Option key={option.value} value={option.value} disabled={option.disabled} data-match={match || undefined}
    className={({ active, selected }) => `admin-select-option ${active ? 'is-active' : ''} ${selected ? 'is-selected' : ''}`}>
    {({ selected }) => <><span>{option.label}</span><span aria-hidden="true">{selected ? '✓' : ''}</span></>}
  </Listbox.Option>;
  // Before it is placed, the menu is see-through, not hidden: Headless UI moves
  // focus into it as it opens, and a hidden menu cannot take focus (then Escape
  // and the arrow keys did nothing).
  const hinting = hint && shown.length > 0;
  return createPortal(<Listbox.Options ref={menu} aria-label={label} className={`admin-select-menu${search ? ' has-search' : ''}${hinting ? ' is-hinting' : ''}`}
    style={position || { opacity: 0, top: 0, left: 0 }} onMouseMove={hint ? () => setHint(false) : undefined}>
    {search && <div className="admin-select-head">
      {pinned.map(option => item(option, false))}
      <label className="admin-select-search">
        <Search size={14} aria-hidden="true" />
        <input ref={field} type="text" value={query} onChange={e => { setQuery(e.target.value); setHint(Boolean(e.target.value.trim())); }} onKeyDown={onKeyDown}
          placeholder={search} aria-label={search} autoComplete="off" spellCheck={false} />
      </label>
    </div>}
    {shown.map(option => item(option, Boolean(search)))}
    {search && !shown.length && <div className="admin-select-empty">No match for “{query.trim()}”</div>}
  </Listbox.Options>, document.body);
}
